import {Components} from '@mui/material';
import {createTheme, Theme} from '@mui/material/styles';
import {ThemeMode} from '../enums/theme-mode';
import {ThemeSettings} from '../interfaces/theme-settings';
import {appSizes} from './../utils/constants/sizes';

// Builder Demo
// https://bareynol.github.io/mui-theme-creator/

// Define light theme colors
// https://material.io/resources/color/#!/?view.left=0&view.right=1&primary.color=00A3FF&secondary.color=ffc32a&primary.text.color=ffffff&secondary.text.color=ffffff

// Define figma local styles
export const Pallete = {
  blue: {
    // figma: blue-base
    base: '#00A3FF',
    // figma: light-blue | stroke
    light: '#B4DAFC',
    // figma: blue-hover | buttons
    lighter: '#EAF1FF',
    // fima: blue-hover | buttons
    hover: '#eff5ff',
    // figma: bg-seniormenu
    bgseniormenu: '#F9FBFF',
  },
  gray: {
    // figma: darkgray
    base: '#464646',

    // figma: gray | general
    general: '#B6B6B6',

    // figma: lightgray
    light: '#B8B8B7',

    // figma: arbitrary
    lighter: '#F8FAFC',

    // figma: bg-lightgray2
    lightbg: '#E4E3E3',

    // figma: gray-disable
    grayDisable: '#A0A0A0',

    // figma: gray-input
    grayInput: '#8A8A8A',
  },
  green: {
    // figma: green-base
    base: '#179577',
  },
  red: {
    // figma: red-base
    base: '#CD1420',
    light: '#FFD7D7',
  },
  yellow: {
    // figma: yellow-base
    base: '#D79C06',
  },
  white: {
    // figma: arbitrary for flat input's
    darker: '#f5f5f5',

    // figma: white-base
    base: '#FFFFFF',
  },
} as const;

const components: Components<Omit<Theme, 'components'>> = {
  MuiInputBase: {
    styleOverrides: {
      root: {
        '&.Mui-disabled': {
          '&:before': {
            borderBottomStyle: 'solid!important',
          },
        },
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '.MuiFilledInput-root': {
          '&:after, &:before': {
            borderBottom: `2px solid ${Pallete.gray.lightbg}`,
          },
          '&:hover, &:active': {
            filter: 'brightness(95%)',
            '&:after, &:before': {
              borderBottom: `2px solid ${Pallete.gray.lightbg}`,
            },
          },
          '&.Mui-disabled': {
            '&:hover, &:active': {
              filter: 'unset',
            },
          },
        },
        '.Mui-focused': {
          '&:after, &:before': {
            borderBottom: `2px solid ${Pallete.gray.lightbg}`,
          },
          '&:hover, &:active': {
            filter: 'brightness(95%)',
            '&:after, &:before': {
              borderBottom: `2px solid ${Pallete.gray.lightbg}`,
            },
          },
        },
      },
    },
    variants: [
      {
        props: {variant: 'filled', color: 'gray'},
        style: {
          '.MuiFilledInput-root': {
            backgroundColor: Pallete.white.darker,
            '&.Mui-disabled': {
              backgroundColor: Pallete.white.darker,
              color: Pallete.gray.base,
              '&:before': {
                borderBottomStyle: 'solid',
              },
            },
            '&:hover, &:active': {
              backgroundColor: Pallete.white.darker,
            },
          },
        },
      },
    ],
  },
  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    variants: [
      {
        props: {size: 'large'},
        style: {
          height: '40px',
          fontSize: '16px',
        },
      },
      {
        props: {variant: 'inverted'},
        style: {
          color: Pallete.blue.base,
          backgroundColor: Pallete.white.base,
          border: `1px solid ${Pallete.white.base}`,
          ':hover': {
            backgroundColor: Pallete.blue.lighter,
            border: `1px solid ${Pallete.blue.base}`,
          },
          '&:active': {
            color: Pallete.white.base,
            backgroundColor: Pallete.blue.base,
            border: `1px solid ${Pallete.blue.base}`,
          },
          '&.Mui-disabled': {
            color: '#B6B6B6',
            backgroundColor: '#E0E0E0',
            border: '1px solid #E0E0E0',
          },
        },
      },
    ],
  },
  MuiLink: {
    defaultProps: {
      underline: 'none',
    },
  },
  MuiAlert: {
    defaultProps: {
      variant: 'filled',
    },
    styleOverrides: {
      root: {
        color: Pallete.gray.base,
        width: '100%',
        padding: '0 4px',
        borderRadius: '0 5px 5px 0',
        border: 0,
        backgroundColor: 'white',
        '.MuiAlert-message': {
          padding: '6px 0',
          lineHeight: '20px',
          fontSize: '14px',
          alignSelf: 'center',
          fontWeight: 400,
        },
        '.MuiAlert-action': {
          marginRight: 0,
          padding: 0,
          alignItems: 'center',
          alignSelf: 'center',
          height: '32px',
          width: '32px',
        },
        '.MuiAlert-icon': {
          padding: '0 10px',
          width: '32px',
          fontSize: '16px',
          alignSelf: 'center',
          justifyContent: 'center',
          alignItems: 'center',
          flexShrink: 0,
          height: '44px',
          marginRight: '4px',
          '> svg': {
            flexGrow: 1,
            flexShrink: 0,
          },
        },
      },
    },
    variants: [
      {
        props: {
          variant: 'outlined',
        },
        style: {
          boxShadow: '2px 8px 16px 0px rgba(60, 60, 60, 0.12)',
          '.MuiAlert-action': {
            color: Pallete.gray.base,
          },
        },
      },
      {
        props: {severity: 'info', variant: 'outlined'},
        style: {
          border: `2px solid ${Pallete.blue.base}`,
          borderLeft: `4px solid ${Pallete.blue.base}`,
          '.MuiAlert-icon': {
            color: Pallete.blue.base,
          },
        },
      },
      {
        props: {severity: 'info', variant: 'filled'},
        style: {
          color: Pallete.blue.base,
          backgroundColor: '#E9F1FF',
          border: `2px solid ${Pallete.blue.base}`,
          borderLeft: `4px solid ${Pallete.blue.base}`,
          '.MuiAlert-icon': {
            color: Pallete.blue.base,
          },
        },
      },
      {
        props: {severity: 'success', variant: 'outlined'},
        style: {
          border: `2px solid ${Pallete.green.base}`,
          borderLeft: `4px solid ${Pallete.green.base}`,
          '.MuiAlert-icon': {
            color: Pallete.green.base,
          },
        },
      },
      {
        props: {severity: 'success', variant: 'filled'},
        style: {
          color: Pallete.green.base,
          backgroundColor: '#F4FFF6',
          border: `2px solid ${Pallete.green.base}`,
          borderLeft: `4px solid ${Pallete.green.base}`,
          '.MuiAlert-icon': {
            color: Pallete.green.base,
          },
        },
      },
      {
        props: {severity: 'error', variant: 'outlined'},
        style: {
          border: `2px solid ${Pallete.red.base}`,
          borderLeft: `4px solid ${Pallete.red.base}`,
          '.MuiAlert-icon': {
            color: Pallete.red.base,
          },
        },
      },
      {
        props: {severity: 'error', variant: 'filled'},
        style: {
          color: Pallete.red.base,
          backgroundColor: '#FFF6F8',
          border: `2px solid ${Pallete.red.base}`,
          borderLeft: `4px solid ${Pallete.red.base}`,
          '.MuiAlert-icon': {
            color: Pallete.red.base,
          },
        },
      },
      {
        props: {severity: 'warning', variant: 'outlined'},
        style: {
          border: `2px solid ${Pallete.yellow.base}`,
          borderLeft: `4px solid ${Pallete.yellow.base}`,
          '.MuiAlert-icon': {
            color: Pallete.yellow.base,
          },
        },
      },
      {
        props: {severity: 'warning', variant: 'filled'},
        style: {
          color: Pallete.yellow.base,
          backgroundColor: '#FFFFEC',
          border: `2px solid ${Pallete.yellow.base}`,
          borderLeft: `4px solid ${Pallete.yellow.base}`,
          '.MuiAlert-icon': {
            color: Pallete.yellow.base,
          },
        },
      },
    ],
  },
};

export const bluebirdLightTheme: Theme = createTheme({
  typography: {
    fontFamily: 'Roboto, sans-serif',
  },
  palette: {
    mode: 'light',
    background: {
      default: '#F3F4F9',
    },
    primary: {
      main: '#00A3FF',
      light: '#B4DAFC',
      dark: '#0075cb',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#D79C06',
      light: '#ffF0C7',
      dark: '#ad7d03',
      contrastText: '#ffffff',
    },
    success: {
      main: '#A2BD15',
      light: '#d6ef52',
      dark: '#6f8d00',
      contrastText: '#ffffff',
    },
    info: {
      main: '#55c5ce',
      light: '#8cf8ff',
      dark: '#04949d',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#FAB843',
      light: '#ffea74',
      dark: '#c38804',
      contrastText: '#ffffff',
    },
    error: {
      main: '#CD1420',
      light: '#ff786d',
      dark: '#a2001b',
      contrastText: '#ffffff',
    },
    // https://mui.com/material-ui/customization/palette/#custom-colors
    neutral: {
      main: '#707070',
      dark: '#454545',
      light: '#9e9e9e',
      contrastText: '#ffffff',
    },
    control: {
      main: '#ffffff',
      dark: '#cccccc',
      light: '#ffffff',
      contrastText: '#ffffff',
    },
    blue: {
      main: '#00A3FF',
      light: '#B4DAFC',
      lighter: '#EAF1FF',
      hover: '#eff5ff',
      bg: '#F9FBFF',
    },
    gray: {
      main: '#464646',
      general: '#B6B6B6',
      light: '#B8B8B7',
      lighter: '#F8FAFC',
      bg: '#E4E3E3',
      disable: '#A0A0A0',
      input: '#8A8A8A',
    },
  },
  components,
});

// define dark theme colors
// https://material.io/resources/color/#!/?view.left=0&view.right=1&primary.color=00A3FF&secondary.color=ffc32a&primary.text.color=ffffff&secondary.text.color=ffffff
export const bluebirdDarkTheme: Theme = createTheme({
  typography: {
    fontFamily: 'Roboto, sans-serif',
  },
  palette: {
    mode: 'dark',
    background: {
      default: '#121212',
    },
    primary: {
      main: '#00A3FF',
      light: '#B4DAFC',
      dark: '#0075cb',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#D79C06',
      light: '#fff561',
      dark: '#ad7d03',
      contrastText: '#ffffff',
    },
    success: {
      main: '#A2BD15',
      light: '#d6ef52',
      dark: '#6f8d00',
      contrastText: '#ffffff',
    },
    info: {
      main: '#55c5ce',
      light: '#8cf8ff',
      dark: '#04949d',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#FAB843',
      light: '#ffea74',
      dark: '#c38804',
      contrastText: '#ffffff',
    },
    error: {
      main: '#CD1420',
      light: '#ff786d',
      dark: '#a2001b',
      contrastText: '#ffffff',
    },
    // https://mui.com/customization/palette/#adding-new-colors
    neutral: {
      main: '#707070',
      dark: '#454545',
      light: '#9e9e9e',
      contrastText: '#ffffff',
    },
    control: {
      main: '#ffffff',
      dark: '#cccccc',
      light: '#ffffff',
      contrastText: '#ffffff',
    },
    blue: {
      main: '#00A3FF',
      light: '#B4DAFC',
      lighter: '#EAF1FF',
      hover: '#eff5ff',
      bg: '#F9FBFF',
    },
    gray: {
      main: '#464646',
      general: '#B6B6B6',
      light: '#B8B8B7',
      lighter: '#F8FAFC',
      bg: '#E4E3E3',
      disable: '#A0A0A0',
      input: '#8A8A8A',
    },
  },
  components,
});

export const bluebirdThemeSettings: ThemeSettings = {
  caregiversAppName: 'Sioslife',
  seniorsDeviceName: 'Sioslife tablet',
  defaultThemeMode: ThemeMode.Light,
  loginLogoStickWidth: 45,
  loginLogoStickHeight: 68,
  loginLogoHeight: 47,
  homeLogoHeight: 36,
  logoWidthHeightRatio: 3.275862068965517,
  drawerWidth: appSizes.sideHamburguerMenu.width.open,
  headerHeight: 56,
  pendingTasksHeight: 66,
};
